import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  EnvelopeSimple,
  ChatCircleText,
  Question,
  CheckCircle,
  ArrowRight,
} from "@phosphor-icons/react";
import { submitContactLead } from "../lib/billing";

// Three teaser FAQs only — full list lives on /faqs
const FAQ_PREVIEW = [
  {
    q: "How do conversions work?",
    a: "1 credit = 1 conversion. Every registered account gets 2 free conversions each month (reset on the 1st, no roll-over). Need more? Buy a once-off conversion pack — those credits never expire.",
  },
  {
    q: "What conversion packs are available?",
    a: "Starter (R15) = 5 conversions, Standard (R35) = 15, Pro (R69) = 35 (best value), and Studio (R149) = 100. All one-time purchases — no subscription, no recurring billing.",
  },
  {
    q: "Do credits expire?",
    a: "Free monthly conversions reset on the 1st and don't roll over. Paid conversions from a pack never expire — buy them once, use them whenever.",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContactLead(form);
      setSent(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1240px] mx-auto" data-testid="contact-page">
      {/* Hero */}
      <section className="border-b border-[var(--border)] px-6 md:px-10 py-14 md:py-20">
        <div className="micro-label flex items-center gap-2">
          <ChatCircleText size={12} weight="bold" /> / Get in touch
        </div>
        <h1 className="font-display font-black tracking-tight leading-[0.95] text-5xl md:text-6xl mt-3">
          Hit us with your<br />
          <span className="text-[var(--accent)]">questions</span> or feedback.
        </h1>
        <p className="mt-6 text-[var(--fg-2)] max-w-xl">
          We read every message and reply within one business day. For urgent
          billing questions please include your account email.
        </p>
        <div
          className="mt-8 hairline bg-[var(--bg-2)] p-5 md:p-6 max-w-xl"
          data-testid="contact-support-callout"
        >
          <div className="micro-label">/ Need support fast?</div>
          <p className="mt-2 text-sm text-[var(--fg-2)]">
            Email us directly — it's the quickest way to reach a human,
            especially for payments, missing conversions or refunds.
          </p>
          <a
            href="mailto:support@decallabs.co.za"
            className="btn-accent mt-4 inline-flex items-center gap-2"
            data-testid="contact-support-email-cta"
          >
            <EnvelopeSimple size={14} weight="bold" />
            support@decallabs.co.za
          </a>
        </div>
      </section>

      {/* Form + email */}
      <section className="grid md:grid-cols-[1.2fr_1fr] gap-px bg-[var(--border)] border-b border-[var(--border)]">
        <div className="bg-white p-6 md:p-10">
          {sent ? (
            <div className="text-center py-16" data-testid="contact-sent">
              <CheckCircle size={48} weight="fill" className="mx-auto text-[var(--success)]" />
              <div className="font-display font-bold text-2xl mt-4">
                Message received
              </div>
              <p className="text-sm text-[var(--fg-2)] mt-2 max-w-sm mx-auto">
                Thanks for reaching out. We'll reply to your email within one business day.
              </p>
              <p className="text-sm text-[var(--fg-2)] mt-4 max-w-sm mx-auto">
                Need a faster answer? Email{" "}
                <a
                  href="mailto:support@decallabs.co.za"
                  className="font-mono text-[var(--fg)] border-b-2 border-[var(--accent)] hover:text-[var(--accent)]"
                  data-testid="contact-sent-email-link"
                >
                  support@decallabs.co.za
                </a>{" "}
                directly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div>
                <label className="micro-label block mb-1">Name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:outline-none focus:border-black"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="micro-label block mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:outline-none focus:border-black"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="micro-label block mb-1">Subject</label>
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:outline-none focus:border-black"
                  data-testid="contact-subject"
                  placeholder="What's this about?"
                />
              </div>
              <div>
                <label className="micro-label block mb-1">Message</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full p-3 border border-[var(--border)] bg-white font-sans text-sm focus:outline-none focus:border-black resize-none"
                  data-testid="contact-message"
                  placeholder="Tell us what you need help with…"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-accent w-full justify-center"
                data-testid="contact-submit"
              >
                {loading ? "Sending…" : "Send message"} <ArrowRight size={14} weight="bold" />
              </button>
              <p className="text-xs text-[var(--fg-2)] text-center">
                Prefer email? Write to{" "}
                <a
                  href="mailto:support@decallabs.co.za"
                  className="font-mono text-[var(--fg)] underline"
                  data-testid="contact-form-email-link"
                >
                  support@decallabs.co.za
                </a>
              </p>
            </form>
          )}
        </div>

        <div className="bg-[var(--bg-2)] p-6 md:p-10">
          <div className="micro-label">/ Other ways</div>
          <h2 className="font-display font-black text-3xl mt-2 leading-tight">
            Prefer email?
          </h2>
          <a
            href="mailto:support@decallabs.co.za"
            className="mt-5 inline-flex items-center gap-2 font-mono text-sm border-b-2 border-[var(--accent)] pb-0.5 hover:text-[var(--accent)]"
            data-testid="contact-email-link"
          >
            <EnvelopeSimple size={14} weight="bold" />
            support@decallabs.co.za
          </a>
          <div className="mt-10 micro-label">/ Response time</div>
          <p className="mt-2 text-sm text-[var(--fg-2)]">
            We aim to reply within <span className="font-mono text-[var(--fg)]">1 business day</span>.
            Billing & refunds get priority.
          </p>
        </div>
      </section>

      {/* FAQs — preview only, full list lives on /faqs */}
      <section className="px-6 md:px-10 py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="micro-label">/ Quick answers</div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-2">
              Common questions
            </h2>
          </div>
          <Link
            to="/faqs"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="contact-see-all-faqs"
          >
            See all FAQs <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
          {FAQ_PREVIEW.map((f, i) => (
            <details
              key={i}
              className="group bg-white p-5 cursor-pointer"
              data-testid={`faq-${i}`}
            >
              <summary className="flex items-start gap-3 list-none">
                <Question size={18} weight="bold" className="text-[var(--accent)] mt-0.5 shrink-0" />
                <span className="font-display font-bold text-base flex-1">{f.q}</span>
                <span className="font-mono text-xs group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-sm text-[var(--fg-2)] mt-3 pl-8 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
